import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const reviewVideoSkill: Skill = {
    name: SKILL_NAMES.reviewVideo,
    description:
        'The video review procedure. Load BEFORE starting any review — the review phase of every build, or when asked to review or polish a video.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Reviewing a Video

The review is pass/fail against a closed defect list — not a subjective score — so
it terminates. A defect \`${TOOL_NAMES.reviewSceneImages}\` returns obligates you to fix
it and re-check, never to note it and move on.

## Procedure
1. **Render everything**: \`${TOOL_NAMES.reviewSceneImages}\` on the scenes (several
   \`scene_ids\` per call).
2. **Fix all failures in one batch** (fixes on different scenes are independent).
3. **Re-render only the scenes you changed and re-check.** Repeat until clean.
4. **Lint**

## What the tool doesn't check
A few things stay yours to judge, because they're about whether the words are the
right words, not about what the frame looks like:
- On a text scene (no narration): does the on-screen text communicate a complete
  thought on its own? (Not required on a voiceover/talking scene — the narration
  carries the thought there.)
- On a voiceover scene: review each overlay separately, cross-check its
  on-screen text word-by-word against the words it covers, and confirm split
  points read naturally. No check on the closed list expresses word-by-word
  overlay/narration correspondence or split-point naturalness, so this has
  no tool-side equivalent and stays a manual check every time.
- **Text that was never meant to be user-facing is still a defect** — a stray
  note, an instruction, or copy left at the template's default. Nothing on the
  closed list covers it: on screen it is indistinguishable from the imperative
  copy a marketing video is made of, and in the transcript the reviewer never
  sees it at all. \`${TOOL_NAMES.lintVideo}\` flags untouched template defaults;
  read the transcript yourself for anything else.

**Swap the design instead of degrading the copy.** If making a scene's text fit
has taken more than ~2 rewrites, or the words only fit as disjointed fragments,
the design is wrong for that content. Pick a different, more suitable scene design and
delete/recreate the scene.

**The tool releases a scene on its own after 3 fix rounds:** a finding that keeps
recurring stops blocking and is reported as accepted-with-residual instead. That is
mechanical, not something you need to track or decide — keep fixing for as long as
the tool keeps blocking, and don't stop early on your own judgment that a defect is
unfixable.

## Lint pass
Run \`${TOOL_NAMES.lintVideo}\` and fix findings. Then verify what lint can't by inspecting the video state:
1. **Narration present?** A voiceover/talking-head/recorded group exists — unless
   the user explicitly opted out.
2. **Every scene built?** No empty or half-built scenes.
3. **Lists and numbers hold up?** Counts match, no dropped items, referenced
   numbers visible.
4. **Text legible and self-standing?** No tiny type, no cryptic fragments
5. **Does it look good?** Opens on an effective intro/title scene; list scenes use list designs;
   quote designs hold real quotations with the speaker credited in the name/role
   slots, not ordinary copy with those slots left empty; media fits and looks authentic.
6. **A complete story?** Read the whole video as a viewer — narration end to end
   plus on-screen text and media in order. It opens with a real hook, flows, and
   closes with a real outro rather than stopping mid-thought.

If any check fails, the video isn't done. Keep fixing and re-verifying until every
check passes and it's clean and render-ready.`;
    },
};
